import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, selectedType, selectedStatus]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert("Failed to load users from server.");
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {

   if(!users || !Array.isArray(users)) return;

    let filtered = [...users];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(user => {

       const nameMatch= user.full_name?.toLowerCase().includes(search);
        const emailMatch = user.email?.toLowerCase().includes(search);
        const mobileMatch= user.mobile_number?.includes(search);
        return nameMatch || emailMatch || mobileMatch;
    });
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(user => user.user_type === selectedType);
    }

    if (selectedStatus !== 'all') {
      const isActive = selectedStatus === 'active';
      filtered = filtered.filter(user => user.is_active === isActive);
    }

    setFilteredUsers(filtered);
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      // Calls your new adminApi.js
      await adminApi.updateUserStatus(userId, !currentStatus);
      
      // Update local state so UI refreshes immediately
      setUsers(users.map(user =>
        user.id === userId ? { ...user, is_active: !currentStatus } : user
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
      alert("Could not update user status.");
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Mobile', 'Type', 'Date', 'Status'];
    const csvData = filteredUsers.map(user => [
      user.full_name || 'N/A',
      user.email,
      user.mobile_number || 'N/A',
      user.user_type || 'full',
      new Date(user.created_at).toLocaleDateString(),
      user.is_active ? 'Active' : 'Inactive'
    ]);

    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export.csv`;
    a.click();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <UsersIcon className="h-7 w-7 mr-3 text-blue-400" />
              User Management
            </h1>
            <p className="text-gray-400 mt-1">Real-time database access</p>
          </div>
          <button onClick={exportToCSV} className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all">
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
          <p className="text-sm text-gray-400">Total Users</p>
          <p className="text-2xl font-bold text-white">{users.length}</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
          <p className="text-sm text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-400">{users.filter(u => u.is_active).length}</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
          <p className="text-sm text-gray-400">Inactive</p>
          <p className="text-2xl font-bold text-red-400">{users.filter(u => !u.is_active).length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search name, email or mobile..."
              className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select 
            className="bg-gray-900/50 border border-gray-700 rounded-lg text-white px-4 py-2 outline-none"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="USER">user</option>
            <option value="TEAM_LEADER">Leader</option>
            <option value="ADMIN">Admin</option>
          </select>

          <select 
            className="bg-gray-900/50 border border-gray-700 rounded-lg text-white px-4 py-2 outline-none"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-900/50 text-gray-400 text-xs uppercase">
            <tr>
              <th className="px-6 py-4 text-left">User Details</th>
              <th className="px-6 py-4 text-left">Mobile</th>
              <th className="px-6 py-4 text-left">Role</th>
              <th className="px-6 py-4 text-left">Status</th>
              <th className="px-6 py-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading ? (
              <tr><td colSpan="5" className="py-10 text-center text-gray-400 italic">Fetching from database...</td></tr>
            ) : filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-700/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="text-white font-medium">{user.full_name}</div>
                  <div className="text-gray-400 text-xs">{user.email}</div>
                </td>
                <td className="px-6 py-4 text-gray-300 text-sm font-mono">
                  {user.mobile_number || 'N/A'}
                </td>
                <td className="px-6 py-4">
                  <span className="text-xs px-2 py-1 rounded bg-blue-900/30 text-blue-300 border border-blue-800">
                    {user.user_type}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded flex items-center w-fit ${user.is_active ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                    {user.is_active ? <CheckCircleIcon className="h-3 w-3 mr-1"/> : <XCircleIcon className="h-3 w-3 mr-1"/>}
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button 
                    onClick={() => toggleUserStatus(user.id, user.is_active)}
                    className={`text-xs px-3 py-1 rounded-md border ${user.is_active ? 'border-red-700 text-red-400 hover:bg-red-900/20' : 'border-green-700 text-green-400 hover:bg-green-900/20'}`}
                  >
                    {user.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsers;